import { useEffect, useState } from "react";
import { IDriver, IRegisterDriver, IUpdateDriver } from "../models/IDriver";
import {
  getMyRestaurantDrivers,
  getDriverById,
  registerDriverUser,
  updateDriver,
  deleteDriver,
  updateDriverStatus,
} from "../services/driver";
import DriversMap from "../components/DriversMap";

// Kurye panel konumu 15 sn'de bir gönderiyor; harita da aynı sıklıkta yenilenir.
const MAP_REFRESH_MS = 15000;

const DRIVER_STATUSES = ["Offline", "Available", "OnDelivery"] as const;
type DriverStatus = typeof DRIVER_STATUSES[number];

const STATUS_META: Record<DriverStatus, { bg: string; color: string; border: string; dot: string; label: string }> = {
  Offline:    { bg: "#f3f4f6", color: "#6b7280", border: "#e5e7eb", dot: "#9ca3af", label: "Offline" },
  Available:  { bg: "#dcfce7", color: "#166534", border: "#86efac", dot: "#16a34a", label: "Müsait" },
  OnDelivery: { bg: "#fef9c3", color: "#854d0e", border: "#fde047", dot: "#ca8a04", label: "Teslimatta" },
};

function normalizeStatus(s: string): DriverStatus {
  const found = DRIVER_STATUSES.find((k) => k.toLowerCase() === (s ?? "").toLowerCase());
  return found ?? "Offline";
}

function DriversPage() {
  const [drivers, setDrivers] = useState<IDriver[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editDriver, setEditDriver] = useState<IDriver | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<IDriver | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [statusMenuFor, setStatusMenuFor] = useState<string | null>(null);
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null);

  const [showMap, setShowMap] = useState(true);

  useEffect(() => {
    fetchDrivers();
  }, []);

  // Sessiz yenileme: kartları spinner'a çevirmeden sadece konum/durum bilgisini tazeler.
  useEffect(() => {
    if (!showMap) return;
    const id = window.setInterval(() => {
      if (document.hidden) return;
      getMyRestaurantDrivers()
        .then((res) => { if (Array.isArray(res.data)) setDrivers(res.data); })
        .catch(() => {});
    }, MAP_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [showMap]);

  const fetchDrivers = () => {
    setLoading(true);
    getMyRestaurantDrivers()
      .then((res) => setDrivers(Array.isArray(res.data) ? res.data : []))
      .catch(() => setDrivers([]))
      .finally(() => setLoading(false));
  };

  const handleCardClick = (driver: IDriver) => {
    setEditLoading(true);
    setEditDriver(driver);
    getDriverById(driver.id)
      .then((res) => setEditDriver(res.data))
      .catch(() => {})
      .finally(() => setEditLoading(false));
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setActionLoading(true);
    try {
      await deleteDriver(deleteConfirm.id);
      setDeleteConfirm(null);
      fetchDrivers();
    } catch {
    } finally {
      setActionLoading(false);
    }
  };

  const vehicleIcon = (type: string) => {
    const t = type?.toLowerCase();
    if (t === "motor" || t === "motorsiklet") return "bi-bicycle";
    if (t === "bisiklet") return "bi-bicycle";
    return "bi-car-front-fill";
  };

  const handleStatusChange = (driver: IDriver, status: DriverStatus) => {
    setStatusMenuFor(null);
    if (normalizeStatus(driver.status) === status) return;
    setUpdatingStatusId(driver.id);
    updateDriverStatus(driver.id, status)
      .then(() => {
        setDrivers((prev) => prev.map((d) => (d.id === driver.id ? { ...d, status } : d)));
      })
      .catch(() => {})
      .finally(() => setUpdatingStatusId(null));
  };

  const onlineCount = drivers.filter((d) => normalizeStatus(d.status) !== "Offline").length;

  return (
    <div className="drv-page">
      {/* Header */}
      <div className="drv-header">
        <div className="drv-header__left">
          <div className="drv-header__icon">
            <i className="bi bi-truck"></i>
          </div>
          <div>
            <h4 className="drv-header__title">Kuryeler</h4>
            <p className="drv-header__subtitle">Kurye ekibi yönetimi</p>
          </div>
        </div>
        <button className="drv-add-btn" onClick={() => setShowAddModal(true)}>
          <i className="bi bi-plus-lg"></i>Kurye Ekle
        </button>
      </div>

      {/* Stats */}
      <div className="drv-stats">
        <div className="drv-stat-card">
          <i className="bi bi-people-fill" style={{ color: "#6366f1" }}></i>
          <span className="drv-stat-count">{drivers.length}</span>
          <span className="drv-stat-label">Toplam</span>
        </div>
        <div className="drv-stat-card drv-stat-card--online">
          <i className="bi bi-circle-fill" style={{ color: "#16a34a", fontSize: ".6rem" }}></i>
          <span className="drv-stat-count">{onlineCount}</span>
          <span className="drv-stat-label">Online</span>
        </div>
      </div>

      {/* Canlı kurye haritası */}
      {!loading && drivers.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, gap: 8, flexWrap: "wrap" }}>
            <div style={{ fontWeight: 700, fontSize: ".9rem", color: "#111827" }}>
              <i className="bi bi-geo-alt-fill me-2" style={{ color: "#dc2626" }}></i>Kuryeler Nerede?
              <span style={{ fontWeight: 500, fontSize: ".75rem", color: "#9ca3af", marginLeft: 8 }}>15 sn'de bir güncellenir</span>
            </div>
            <button
              onClick={() => setShowMap((s) => !s)}
              style={{ background: "#fff", border: "1.5px solid #e5e7eb", borderRadius: 8, padding: "4px 12px", fontSize: ".78rem", fontWeight: 600, color: "#374151", cursor: "pointer" }}
            >
              <i className={`bi ${showMap ? "bi-eye-slash" : "bi-map"} me-1`}></i>{showMap ? "Haritayı Gizle" : "Haritayı Göster"}
            </button>
          </div>
          {showMap && <DriversMap drivers={drivers} height="min(60vh, 420px)" />}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" />
        </div>
      )}

      {/* Empty */}
      {!loading && drivers.length === 0 && (
        <div className="drv-empty">
          <i className="bi bi-truck"></i>
          <div>Henüz kurye eklenmemiş</div>
          <button className="drv-add-btn drv-add-btn--sm" onClick={() => setShowAddModal(true)}>
            <i className="bi bi-plus-lg"></i>İlk Kuryeyi Ekle
          </button>
        </div>
      )}

      {/* Driver cards */}
      {!loading && drivers.length > 0 && (
        <div className="drv-grid">
          {drivers.map((d) => {
            const currentStatus = normalizeStatus(d.status);
            const meta = STATUS_META[currentStatus];
            const menuOpen = statusMenuFor === d.id;
            const isUpdating = updatingStatusId === d.id;
            return (
              <div key={d.id} className="drv-card" onClick={() => handleCardClick(d)}>
                <div className="drv-card__header">
                  <div className="drv-card__avatar">
                    {d.driverName?.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2)}
                  </div>
                  <div className="drv-card__info">
                    <div className="drv-card__name">{d.driverName}</div>
                    {d.driverPhone && (
                      <div className="drv-card__phone">
                        <i className="bi bi-telephone-fill"></i>{d.driverPhone}
                      </div>
                    )}
                  </div>
                  <div className="drv-status-wrap" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className="drv-status-badge drv-status-badge--btn"
                      style={{ background: meta.bg, color: meta.color, border: `1px solid ${meta.border}` }}
                      onClick={() => setStatusMenuFor(menuOpen ? null : d.id)}
                      disabled={isUpdating}
                    >
                      {isUpdating ? (
                        <span className="spinner-border spinner-border-sm" style={{ width: 10, height: 10 }} />
                      ) : (
                        <>
                          <span className="drv-status-dot" style={{ background: meta.dot }} />
                          {meta.label}
                          <i className="bi bi-chevron-down" style={{ fontSize: ".62rem" }}></i>
                        </>
                      )}
                    </button>
                    {menuOpen && (
                      <>
                        <div className="drv-status-menu-overlay" onClick={() => setStatusMenuFor(null)} />
                        <div className="drv-status-menu">
                          {DRIVER_STATUSES.map((key) => (
                            <button
                              type="button"
                              key={key}
                              className={`drv-status-menu__item${currentStatus === key ? " is-active" : ""}`}
                              onClick={() => handleStatusChange(d, key)}
                            >
                              <span className="drv-status-dot" style={{ background: STATUS_META[key].dot }} />
                              {STATUS_META[key].label}
                              {currentStatus === key && <i className="bi bi-check-lg ms-auto"></i>}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <div className="drv-card__body">
                  <div className="drv-card__detail">
                    <i className={`bi ${vehicleIcon(d.vehicleType)}`}></i>
                    <span>{d.vehicleType || "-"}</span>
                  </div>
                  <div className="drv-card__detail">
                    <i className="bi bi-credit-card-2-front-fill"></i>
                    <span>{d.vehiclePlate || "-"}</span>
                  </div>
                </div>
                <div className="drv-card__footer">
                  <span className="drv-card__date">
                    <i className="bi bi-calendar3"></i>
                    {new Date(d.createdAt).toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" })}
                  </span>
                  <button
                    className="drv-delete-btn"
                    onClick={(e) => { e.stopPropagation(); setDeleteConfirm(d); }}
                    title="Kuryeyi sil"
                  >
                    <i className="bi bi-trash3-fill"></i>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Driver Modal */}
      {showAddModal && (
        <AddDriverModal
          onClose={() => setShowAddModal(false)}
          onSaved={() => { setShowAddModal(false); fetchDrivers(); }}
        />
      )}

      {/* Edit Driver Modal */}
      {editDriver && (
        <EditDriverModal
          driver={editDriver}
          loading={editLoading}
          onClose={() => setEditDriver(null)}
          onSaved={() => { setEditDriver(null); fetchDrivers(); }}
        />
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="drv-modal-backdrop" onClick={() => setDeleteConfirm(null)}>
          <div className="drv-modal" onClick={(e) => e.stopPropagation()}>
            <div className="drv-modal__icon drv-modal__icon--delete">
              <i className="bi bi-trash3-fill"></i>
            </div>
            <h5 className="drv-modal__title">Kuryeyi Sil</h5>
            <p className="drv-modal__desc">
              <strong>{deleteConfirm.driverName}</strong> adlı kuryeyi silmek istediğinize emin misiniz?
            </p>
            <p style={{ color: "#dc2626", fontSize: ".8rem", marginBottom: "20px" }}>Bu işlem geri alınamaz.</p>
            <div className="drv-modal__actions">
              <button className="drv-modal-btn drv-modal-btn--cancel" onClick={() => setDeleteConfirm(null)} disabled={actionLoading}>
                İptal
              </button>
              <button className="drv-modal-btn drv-modal-btn--delete" onClick={handleDelete} disabled={actionLoading}>
                {actionLoading && <span className="spinner-border spinner-border-sm me-2" />}
                Evet, Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Add Driver Modal ────────────────────────────────────────────────────────── */
function AddDriverModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState<IRegisterDriver>({
    customerName: "", customerSurName: "", customerUserName: "",
    customerTelNo: "", customerEmail: "",
    customerBirthDate: "",
    customerIsEnabled: true,
    customerLogin: { customerPassword: "" },
    vehicleType: "motor", vehiclePlate: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (field: Partial<IRegisterDriver>) => setForm((p) => ({ ...p, ...field }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    registerDriverUser(form)
      .then(() => onSaved())
      .catch((err) => {
        const desc = err?.response?.data?.[0]?.description ?? err?.response?.data?.description ?? "Bir hata oluştu.";
        setError(desc);
      })
      .finally(() => setSaving(false));
  };

  return (
    <div className="drv-modal-backdrop" onClick={onClose}>
      <div className="drv-modal drv-modal--form" onClick={(e) => e.stopPropagation()}>
        <div className="drv-modal-header">
          <h5 className="drv-modal__title"><i className="bi bi-plus-circle-fill me-2" style={{ color: "#6366f1" }}></i>Yeni Kurye Ekle</h5>
          <button className="drv-close-btn" onClick={onClose}><i className="bi bi-x-lg"></i></button>
        </div>
        {error && (
          <div className="drv-alert drv-alert--error"><i className="bi bi-exclamation-circle-fill me-1"></i>{error}</div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="drv-form-grid">
            <div className="drv-field">
              <label className="drv-label"><i className="bi bi-person me-1"></i>Ad</label>
              <input className="form-control drv-input" value={form.customerName} onChange={(e) => set({ customerName: e.target.value })} required />
            </div>
            <div className="drv-field">
              <label className="drv-label"><i className="bi bi-person me-1"></i>Soyad</label>
              <input className="form-control drv-input" value={form.customerSurName} onChange={(e) => set({ customerSurName: e.target.value })} required />
            </div>
            <div className="drv-field">
              <label className="drv-label"><i className="bi bi-at me-1"></i>Kullanıcı Adı</label>
              <input className="form-control drv-input" value={form.customerUserName} onChange={(e) => set({ customerUserName: e.target.value })} required />
            </div>
            <div className="drv-field">
              <label className="drv-label"><i className="bi bi-telephone me-1"></i>Telefon</label>
              <input className="form-control drv-input" value={form.customerTelNo} onChange={(e) => set({ customerTelNo: e.target.value })} required />
            </div>
            <div className="drv-field">
              <label className="drv-label"><i className="bi bi-envelope me-1"></i>E-posta</label>
              <input type="email" className="form-control drv-input" value={form.customerEmail} onChange={(e) => set({ customerEmail: e.target.value })} required />
            </div>
            <div className="drv-field">
              <label className="drv-label"><i className="bi bi-lock me-1"></i>Şifre</label>
              <input type="password" className="form-control drv-input" value={form.customerLogin.customerPassword} onChange={(e) => set({ customerLogin: { customerPassword: e.target.value } })} required />
            </div>
            <div className="drv-field">
              <label className="drv-label"><i className="bi bi-calendar-event me-1"></i>Doğum Tarihi</label>
              <input
                type="date"
                className="form-control drv-input"
                value={form.customerBirthDate ? form.customerBirthDate.slice(0, 10) : ""}
                max={new Date(new Date().getFullYear() - 13, new Date().getMonth(), new Date().getDate()).toISOString().slice(0, 10)}
                onChange={(e) => set({ customerBirthDate: e.target.value ? new Date(e.target.value).toISOString() : "" })}
                required
              />
            </div>
            <div className="drv-field">
              <label className="drv-label"><i className="bi bi-truck me-1"></i>Araç Tipi</label>
              <select className="form-select drv-input" value={form.vehicleType} onChange={(e) => set({ vehicleType: e.target.value })}>
                <option value="motor">Motor</option>
                <option value="araba">Araba</option>
                <option value="bisiklet">Bisiklet</option>
              </select>
            </div>
            <div className="drv-field">
              <label className="drv-label"><i className="bi bi-credit-card-2-front me-1"></i>Plaka</label>
              <input className="form-control drv-input" value={form.vehiclePlate} onChange={(e) => set({ vehiclePlate: e.target.value })} required />
            </div>
          </div>
          <div className="drv-modal__actions mt-3">
            <button type="button" className="drv-modal-btn drv-modal-btn--cancel" onClick={onClose} disabled={saving}>İptal</button>
            <button type="submit" className="drv-modal-btn drv-modal-btn--save" disabled={saving}>
              {saving && <span className="spinner-border spinner-border-sm me-2" />}
              <i className="bi bi-check-lg me-1"></i>Kaydet
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Edit Driver Modal ───────────────────────────────────────────────────────── */
function EditDriverModal({ driver, loading: fetchLoading, onClose, onSaved }: { driver: IDriver; loading: boolean; onClose: () => void; onSaved: () => void }) {
  const nameParts = driver.driverName?.split(" ") ?? [];
  const [form, setForm] = useState<IUpdateDriver>({
    customerName: nameParts[0] ?? "",
    customerSurName: nameParts.slice(1).join(" ") ?? "",
    customerEmail: "",
    customerTelNo: driver.driverPhone ?? "",
    customerBirthDate: "",
    vehicleType: driver.vehicleType ?? "",
    vehiclePlate: driver.vehiclePlate ?? "",
  });
  const [newPassword, setNewPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    const parts = driver.driverName?.split(" ") ?? [];
    setForm({
      customerName: parts[0] ?? "",
      customerSurName: parts.slice(1).join(" ") ?? "",
      customerEmail: "",
      customerTelNo: driver.driverPhone ?? "",
      customerBirthDate: "",
      vehicleType: driver.vehicleType ?? "",
      vehiclePlate: driver.vehiclePlate ?? "",
    });
  }, [driver]);

  const set = (field: Partial<IUpdateDriver>) => setForm((p) => ({ ...p, ...field }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");

    const payload: Record<string, string | undefined> = {};
    if (form.customerName) payload.customerName = form.customerName;
    if (form.customerSurName) payload.customerSurName = form.customerSurName;
    if (form.customerEmail) payload.customerEmail = form.customerEmail;
    if (form.customerTelNo) payload.customerTelNo = form.customerTelNo;
    if (form.customerBirthDate) payload.customerBirthDate = form.customerBirthDate;
    if (form.vehicleType) payload.vehicleType = form.vehicleType;
    if (form.vehiclePlate) payload.vehiclePlate = form.vehiclePlate;
    if (newPassword) payload.newPassword = newPassword;

    updateDriver(driver.id, payload)
      .then(() => { setSuccess("Kurye bilgileri güncellendi."); onSaved(); })
      .catch((err) => {
        const desc = err?.response?.data?.[0]?.description ?? err?.response?.data?.description ?? "Bir hata oluştu.";
        setError(desc);
      })
      .finally(() => setSaving(false));
  };

  return (
    <div className="drv-modal-backdrop" onClick={onClose}>
      <div className="drv-modal drv-modal--form" onClick={(e) => e.stopPropagation()}>
        <div className="drv-modal-header">
          <h5 className="drv-modal__title"><i className="bi bi-pencil-square me-2" style={{ color: "#f59e0b" }}></i>Kurye Düzenle</h5>
          <button className="drv-close-btn" onClick={onClose}><i className="bi bi-x-lg"></i></button>
        </div>

        {fetchLoading && (
          <div className="text-center py-4">
            <div className="spinner-border spinner-border-sm text-primary" role="status" />
            <div className="text-muted small mt-2">Bilgiler yükleniyor...</div>
          </div>
        )}

        {error && <div className="drv-alert drv-alert--error"><i className="bi bi-exclamation-circle-fill me-1"></i>{error}</div>}
        {success && <div className="drv-alert drv-alert--success"><i className="bi bi-check-circle-fill me-1"></i>{success}</div>}

        {!fetchLoading && (
          <form onSubmit={handleSubmit}>
            {/* Driver info header */}
            <div className="drv-edit-info">
              <div className="drv-edit-avatar">
                {driver.driverName?.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2)}
              </div>
              <div>
                <div className="fw-bold">{driver.driverName}</div>
                <div className="text-muted" style={{ fontSize: ".78rem" }}>
                  {STATUS_META[normalizeStatus(driver.status)].label} &middot; {driver.vehicleType} &middot; {driver.vehiclePlate}
                </div>
                {driver.driverEmail && (
                  <div className="text-muted" style={{ fontSize: ".78rem" }}>
                    <i className="bi bi-envelope me-1"></i>{driver.driverEmail}
                  </div>
                )}
              </div>
            </div>

            <div className="drv-form-grid">
              <div className="drv-field">
                <label className="drv-label"><i className="bi bi-person me-1"></i>Ad</label>
                <input className="form-control drv-input" value={form.customerName} onChange={(e) => set({ customerName: e.target.value })} />
              </div>
              <div className="drv-field">
                <label className="drv-label"><i className="bi bi-person me-1"></i>Soyad</label>
                <input className="form-control drv-input" value={form.customerSurName} onChange={(e) => set({ customerSurName: e.target.value })} />
              </div>
              <div className="drv-field">
                <label className="drv-label"><i className="bi bi-telephone me-1"></i>Telefon</label>
                <input className="form-control drv-input" value={form.customerTelNo} onChange={(e) => set({ customerTelNo: e.target.value })} />
              </div>
              <div className="drv-field">
                <label className="drv-label"><i className="bi bi-envelope me-1"></i>E-posta</label>
                <input type="email" className="form-control drv-input" value={form.customerEmail} onChange={(e) => set({ customerEmail: e.target.value })} placeholder={driver.driverEmail || "Boş bırakılırsa güncellenmez"} />
              </div>
              <div className="drv-field">
                <label className="drv-label"><i className="bi bi-truck me-1"></i>Araç Tipi</label>
                <select className="form-select drv-input" value={form.vehicleType} onChange={(e) => set({ vehicleType: e.target.value })}>
                  <option value="motor">Motor</option>
                  <option value="araba">Araba</option>
                  <option value="bisiklet">Bisiklet</option>
                </select>
              </div>
              <div className="drv-field">
                <label className="drv-label"><i className="bi bi-credit-card-2-front me-1"></i>Plaka</label>
                <input className="form-control drv-input" value={form.vehiclePlate} onChange={(e) => set({ vehiclePlate: e.target.value })} />
              </div>
              <div className="drv-field drv-field--full">
                <label className="drv-label"><i className="bi bi-lock me-1"></i>Yeni Şifre <span className="text-muted">(Opsiyonel)</span></label>
                <input type="password" className="form-control drv-input" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Değiştirmek istemiyorsanız boş bırakın" />
              </div>
            </div>
            <div className="drv-modal__actions mt-3">
              <button type="button" className="drv-modal-btn drv-modal-btn--cancel" onClick={onClose} disabled={saving}>İptal</button>
              <button type="submit" className="drv-modal-btn drv-modal-btn--save" disabled={saving}>
                {saving && <span className="spinner-border spinner-border-sm me-2" />}
                <i className="bi bi-check-lg me-1"></i>Düzelt
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default DriversPage;
